import React from "react";
import { MenuItem, Select } from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

/*
 * ThemedSelect — drop-in replacement for a native <select>.
 * onChange receives { target: { name, value } } (MUI Select already does this),
 * so existing handleChange functions work as-is.
 *
 *   <ThemedSelect name="subscription" value={org?.subscription}
 *     onChange={handleChange} error={!!errors.subscription}
 *     placeholder="---Choose Type---"
 *     options={[{ value: "FREE", label: "Free" }, { value: "PRO", label: "Pro" }]} />
 */

const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const INK = "#0F1B2A";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";
const ERROR = "#C42B24";

const ThemedSelect = ({
  name,
  id,
  value,
  onChange,
  options = [],
  placeholder,
  error = false,
  disabled = false,
  height = 42,
  fullWidth = true,
  sx,
  menuMaxHeight = 320,
}) => (
  <Select
    id={id}
    name={name}
    value={value ?? ""}
    onChange={onChange}
    displayEmpty
    disabled={disabled}
    fullWidth={fullWidth}
    IconComponent={KeyboardArrowDownRoundedIcon}
    renderValue={(v) => {
      if (v === "" || v === undefined || v === null) {
        return <span style={{ color: "#8A97A8", fontWeight: 500 }}>{placeholder || "Choose"}</span>;
      }
      const hit = options.find((o) => o.value === v);
      return hit ? hit.label : v;
    }}
    MenuProps={{
      anchorOrigin: { vertical: "bottom", horizontal: "left" },
      transformOrigin: { vertical: "top", horizontal: "left" },
      PaperProps: {
        sx: {
          mt: 0.75,
          maxHeight: menuMaxHeight,
          borderRadius: "12px",
          border: `1px solid ${LINE}`,
          boxShadow: "0 16px 36px -12px rgba(15,27,42,0.28)",
          "& .MuiList-root": { p: 0.5 },
          "& .MuiMenuItem-root": {
            fontFamily: FONT,
            fontSize: 13.5,
            fontWeight: 500,
            color: INK,
            borderRadius: "8px",
            minHeight: 38,
            px: 1.25,
            "&:hover": { background: "#F4F7FA" },
          },
          "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover": {
            background: "#EAF2FB",
            color: "#1B4F82",
            fontWeight: 700,
          },
          "& .MuiMenuItem-root.Mui-focusVisible": { background: "#F4F7FA" },
        },
      },
    }}
    sx={{
      height,
      margin: 0,
      borderRadius: "10px",
      background: disabled ? "#F4F7FA" : "#FFFFFF",
      fontFamily: FONT,
      fontSize: 14,
      fontWeight: 600,
      color: INK,
      "& .MuiSelect-select": { display: "flex", alignItems: "center", py: 0, pl: 1.5 },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: error ? ERROR : FIELD_EDGE },
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: error ? ERROR : "#B4C6DA" },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: error ? ERROR : PRIMARY, borderWidth: 1 },
      "& .MuiSelect-icon": { color: MUTED, right: 10 },
      ...sx,
    }}
  >
    {options.map((o) => (
      <MenuItem key={String(o.value)} value={o.value} disabled={o.disabled}>
        {o.label}
      </MenuItem>
    ))}
  </Select>
);

export default ThemedSelect;
